import React, { useEffect, useState } from "react";
import Table from "../../applicationUi/components/Table";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { calculateAge } from "../../utils/calculateAge";
import {
  UsersIcon,
  EyeIcon,
  EyeSlashIcon,
  CalendarIcon,
  UserIcon,
  UserGroupIcon,
  XMarkIcon,
  DocumentTextIcon,
  CreditCardIcon,
  BanknotesIcon,
} from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { uploadFile, fetchUploads } from "../../store/thunks/uploadsThunk";
import { logInfo, logError } from "../../utils/logger";
import {
  fetchTreatingClinician,
  fetchSupervisingClinician,
  fetchClientById,
} from "../../store/store";
import ClientDetails from "./ClientDetails";
import FamilyDetails from "./FamilyDetails";
import ClientInvoices from "./ClientInvoices";
import ClientSoapNotes from "./ClientSoapNotes";
import ClientBlocks from "./ClientBlocks";
import ClientNotes from "./ClientNotes";
import FileUpload from "./FileUpload";
import ClientUploads from "./ClientUploads";
import ClientJournal from "./ClientJournal";
import CircularButton from "../../applicationUi/components/CircularButton";
import { clearSelectedClinicians } from "../../store/slices/cliniciansSlice";
import { clearSelectedClient } from "../../store/slices/clientsSlice";
import ClientAppointments from "./ClientAppointments";
import ClientDeposits from "./ClientDeposits";
import ClientPortal from "./ClientPortal";
import PaymentContractModal from "./PaymentContractModal";
import { updateClient } from "../../store/thunks/clientsThunk";
// import ClientJourneyProgressBar from '../../components/ui/ClientJourneyProgressBar';
// import ClientJourney from './ClientJourney';
// import { clearCurrentJourney } from "../../store/slices/clientJourneySlice";
// import { fetchClientJourneyById } from "../../store/thunks/clientJourneyThunk";
import ClientIntake from "./ClientIntake";

export default function ClientOverview(isModal = false) {
  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState("details");
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const treatingClinician = useSelector(
    (state) => state.clinicians.selectedTreatingClinician
  );
  const supervisingClinician = useSelector(
    (state) => state.clinicians.selectedSupervisingClinician
  );
  const client = useSelector((state) => state.clients.selectedClient);

  useEffect(() => {
    if (client?.treatingClinician) {
      const treatingClinicianId =
        client.treatingClinician.current?._id || client.treatingClinician._id;
      if (treatingClinicianId) {
        dispatch(fetchTreatingClinician(treatingClinicianId));
      }
    }

    if (client?.supervisingClinician) {
      const supervisingClinicianId =
        client.supervisingClinician.current?._id ||
        client.supervisingClinician._id;
      if (supervisingClinicianId) {
        dispatch(fetchSupervisingClinician(supervisingClinicianId));
      }
    }

    // Cleanup function runs only when component unmounts
    return () => {
      dispatch(clearSelectedClinicians());
      dispatch(clearSelectedClient()); // <-- This will now only run when the component fully unmounts
    };
  }, []); // <-- Remove `client` from dependencies to prevent resetting on each update

  const clientAge = client?.dateOfBirth
    ? calculateAge(client.dateOfBirth)
    : "N/A";

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    logInfo("Tab changed", { newTab: tab });
  };

  const handleCopyEmail = () => {
    if (client?.email) {
      navigator.clipboard.writeText(client.email);
      toast.success("Copied to clipboard!");
      logInfo("Client email copied to clipboard", {
        email: client.email,
      });
    }
  };

  const handlePaymentInfoClick = () => {
    setIsPaymentModalOpen(true);
  };

  const handlePaymentInfoSave = async (formData) => {
    try {
      await dispatch(
        updateClient({
          id: client._id,
          clientData: {
            contract: formData.contract,
            defaultPaymentMethod: formData.defaultPaymentMethod,
            creditCardOnFile: formData.creditCardOnFile,
          },
        })
      ).unwrap();

      setSuccessMessage(
        `Payment information updated successfully for ${client.firstName} ${client.lastName}`
      );
      setShowSuccessModal(true);
      setTimeout(() => setShowSuccessModal(false), 2000);
    } catch (error) {
      toast.error("Failed to update payment information");
      logError("Failed to update payment information", { error });
    }
  };

  if (!client) {
    return (
      <div className="w-full h-40 flex items-center justify-center">
        <div className="animate-pulse flex space-x-4">
          <div className="rounded-full bg-gray-200 h-12 w-12"></div>
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-sm shadow-sm border border-gray-200">
      {/* Header Section */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex justify-between items-start">
          {/* Left side: Client Info */}
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold tracking-tight text-gray-900">
                {client?.firstName} {client?.lastName}{" "}
                <span className="text-gray-600">({clientAge})</span>
              </h2>
              <div className="bg-gray-100 p-1 rounded-sm">
                <UsersIcon className="h-4 w-4 text-gray-500" />
              </div>
            </div>
            <div className="text-sm text-gray-500 space-y-0.5 ml-1">
              <p>
                {client?.address?.aptUnit ? `${client.address.aptUnit} -` : ""}{" "}
                {client?.address?.streetNumber} {client?.address?.streetName}
              </p>
              <p>
                {client?.address?.city} {client?.address?.province}{" "}
                {client?.address?.postalCode}
              </p>
              <p>{client?.phoneNumbers[0]?.number}</p>
              <p
                className="text-blue-600 hover:text-blue-800 cursor-pointer transition-colors flex items-center gap-1"
                onClick={handleCopyEmail}>
                {client?.email}
                <span className="text-xs text-gray-400">(click to copy)</span>
              </p>
              <ToastContainer position="bottom-right" autoClose={2000} />
            </div>
          </div>

          {/* Right side: Appointment & Clinician Info */}
          <div className="text-sm space-y-2 bg-gray-50 p-3 rounded-sm">
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-gray-500" />
              <div>
                <span className="text-gray-500">Next Appointment:</span>{" "}
                <span className="text-gray-900 font-medium">
                  {client?.appointments?.[0]?.start
                    ? new Intl.DateTimeFormat("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      }).format(new Date(client.appointments[0].start))
                    : "N/A"}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <UserIcon className="h-4 w-4 text-gray-500" />
              <div>
                <span className="text-gray-500">Treating:</span>{" "}
                <span className="text-gray-900 font-medium">
                  {treatingClinician?.firstName}{" "}
                  {treatingClinician?.lastName || "N/A"}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <UserGroupIcon className="h-4 w-4 text-gray-500" />
              <div>
                <span className="text-gray-500">Supervising:</span>{" "}
                <span className="text-gray-900 font-medium">
                  {supervisingClinician?.firstName}{" "}
                  {supervisingClinician?.lastName || "N/A"}
                </span>
              </div>
            </div>
          </div>

          {/* Right side: Payment & Contract Information */}
          <div
            className="text-sm space-y-2 bg-gray-50 p-3 rounded-sm cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={handlePaymentInfoClick}>
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="h-4 w-4 text-gray-500" />
              <div>
                <span className="text-gray-500">Signed Contract:</span>{" "}
                <span className="text-gray-900 font-medium">
                  {client?.contract?.signed ? "Yes" : "No"}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <BanknotesIcon className="h-4 w-4 text-gray-500" />
              <div>
                <span className="text-gray-500">Default Payment Method:</span>{" "}
                <span className="text-gray-900 font-medium">
                  {client?.defaultPaymentMethod?.name || "N/A"}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <CreditCardIcon className="h-4 w-4 text-gray-500" />
              <div>
                <span className="text-gray-500">Credit Card on File:</span>{" "}
                <span className="text-gray-900 font-medium">
                  {client?.creditCardOnFile ? "Yes" : "No"}
                </span>
              </div>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={() => {
              logInfo("Client overview closed", { clientId: client?._id });
              dispatch(clearSelectedClient());
            }}
            className="rounded-sm p-2 hover:bg-gray-100 transition-colors">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="border-b border-gray-200">
        <nav
          className="flex overflow-x-auto px-4 scrollbar-hide"
          aria-label="Tabs">
          {[
            { id: "details", label: "Client Details" },
            { id: "family", label: "Family Details" },
            { id: "intake", label: "Intake" },
            { id: "invoices", label: "Invoices" },
            { id: "deposits", label: "Deposits" },
            { id: "appointments", label: "Appointments" },
            { id: "blocks", label: "Blocks" },
            { id: "soapNotes", label: "SOAP Notes" },
            { id: "upload", label: "Upload" },
            { id: "files", label: "Files" },
            { id: "notes", label: "Notes" },
            { id: "journal", label: "Journal" },
            { id: "portal", label: "Portal" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={classNames(
                "px-3 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors",
                selectedTab === tab.id
                  ? "border-gray-900 text-gray-900"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              )}
              aria-current={selectedTab === tab.id ? "page" : undefined}>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content Area */}
      <div className="p-4">
        {selectedTab === "details" && <ClientDetails />}
        {selectedTab === "family" && <FamilyDetails />}
        {selectedTab === "intake" && <ClientIntake />}
        {selectedTab === "invoices" && <ClientInvoices />}
        {selectedTab === "deposits" && <ClientDeposits />}
        {selectedTab === "appointments" && <ClientAppointments />}
        {selectedTab === "blocks" && <ClientBlocks />}
        {selectedTab === "soapNotes" && <ClientSoapNotes />}
        {selectedTab === "upload" && <FileUpload />}
        {selectedTab === "files" && <ClientUploads client={client} />}
        {selectedTab === "notes" && <ClientNotes client={client} />}
        {selectedTab === "journal" && <ClientJournal client={client} />}
        {selectedTab === "portal" && <ClientPortal client={client} />}
      </div>

      {/* Add the modal */}
      <PaymentContractModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        client={client}
        onSave={handlePaymentInfoSave}
      />

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">
                  {successMessage}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function for dynamic class names
function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}
